import { lazy, Suspense } from 'react';
import { Outlet, createBrowserRouter, Navigate } from 'react-router';
import { ROUTES } from '@/lib/routes';
import { AppLayout, FullBleedAppLayout } from '@/app/layout/AppLayout';
import { RouteErrorBoundary } from '@/app/components/RouteErrorBoundary';
import { PageSkeleton } from '@/app/components/PageSkeleton';
import { SidebarStateProvider } from '@/app/layout/Sidebar';
import { CAMPAIGNS_ENABLED } from '@/features/config/flags';
import { RequireRole } from '@/app/providers/RequireRole';
import { ROLES } from '@/lib/user-roles';

const LoginPage = lazy(() => import('@/app/pages/LoginPage'));
const NotFoundPage = lazy(() => import('@/app/pages/NotFoundPage'));
const UsageGuidelinesPage = lazy(() => import('@/app/pages/UsageGuidelinesPage'));

const ProductsPage = lazy(() => import('@/features/products/pages/ProductsPage'));
const ProductNewPage = lazy(() => import('@/features/products/pages/ProductNewPage'));
const ProductDetailPage = lazy(() => import('@/features/products/pages/ProductDetailPage'));

const FreeTextPage = lazy(() => import('@/features/free-text/pages/FreeTextPage'));

const EcommercePage = lazy(() => import('@/features/ecommerce/pages/EcommercePage'));
const EcommerceNewPage = lazy(() => import('@/features/ecommerce/pages/EcommerceNewPage'));
const EcommerceDetailPage = lazy(() => import('@/features/ecommerce/pages/EcommerceDetailPage'));

const CampaignsPage = lazy(() => import('@/features/campaigns/pages/CampaignsPage'));
const CampaignNewPage = lazy(() => import('@/features/campaigns/pages/CampaignNewPage'));
const CampaignDetailPage = lazy(() => import('@/features/campaigns/pages/CampaignDetailPage'));
const NewsletterTemplatesPage = lazy(
  () => import('@/features/campaigns/pages/NewsletterTemplatesPage'),
);
const NewsletterTemplateDetailPage = lazy(
  () => import('@/features/campaigns/pages/NewsletterTemplateDetailPage'),
);

const ChatbotPage = lazy(() => import('@/features/chatbot/pages/ChatbotPage'));

function LazySuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

function CampaignAccessLayout() {
  return (
    <RequireRole role={ROLES.CAMPAIGNS}>
      <Outlet />
    </RequireRole>
  );
}

function SidebarProviderLayout() {
  return (
    <SidebarStateProvider>
      <Outlet />
    </SidebarStateProvider>
  );
}

export const router = createBrowserRouter([
  {
    path: ROUTES.ROOT,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.APP} replace />,
      },
      {
        path: 'login',
        element: (
          <LazySuspense>
            <LoginPage />
          </LazySuspense>
        ),
      },
      {
        path: 'app',
        element: <SidebarProviderLayout />,
        children: [
          {
            element: <AppLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [
              {
                index: true,
                element: <Navigate to={ROUTES.PRODUCTS} replace />,
              },
              {
                path: 'products',
                errorElement: <RouteErrorBoundary />,
                children: [
                  { index: true, element: <ProductsPage /> },
                  { path: 'new', element: <ProductNewPage /> },
                  { path: ':id', element: <ProductDetailPage /> },
                ],
              },
              {
                path: 'free-text',
                element: <FreeTextPage />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'ecommerce',
                errorElement: <RouteErrorBoundary />,
                children: [
                  { index: true, element: <EcommercePage /> },
                  { path: 'new', element: <EcommerceNewPage /> },
                  { path: ':id', element: <EcommerceDetailPage /> },
                ],
              },
              ...(CAMPAIGNS_ENABLED
                ? [
                    {
                      path: 'campaigns',
                      element: <CampaignAccessLayout />,
                      errorElement: <RouteErrorBoundary />,
                      children: [
                        { index: true, element: <CampaignsPage /> },
                        { path: 'new', element: <CampaignNewPage /> },
                        { path: 'templates/newsletter', element: <NewsletterTemplatesPage /> },
                      ],
                    },
                  ]
                : []),
              {
                path: 'usage-guidelines',
                element: <UsageGuidelinesPage />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: '*',
                element: <Navigate to={ROUTES.NOT_FOUND} replace />,
              },
            ],
          },
          {
            element: <FullBleedAppLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [
              ...(CAMPAIGNS_ENABLED
                ? [
                    {
                      element: <CampaignAccessLayout />,
                      children: [
                        {
                          path: 'campaigns/templates/newsletter/:templateId',
                          element: <NewsletterTemplateDetailPage />,
                        },
                        {
                          path: 'campaigns/:id',
                          element: <CampaignDetailPage />,
                        },
                      ],
                    },
                  ]
                : []),
              {
                path: 'chatbot',
                children: [
                  { index: true, element: <ChatbotPage /> },
                  { path: ':conversationId', element: <ChatbotPage /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'not-found',
        element: (
          <LazySuspense>
            <NotFoundPage />
          </LazySuspense>
        ),
      },
      {
        path: '*',
        element: <Navigate to={ROUTES.NOT_FOUND} replace />,
      },
    ],
  },
]);
